/**
 * Colour arithmetic for the card artwork, in hex so both renderers read it.
 * A design picks two colours; every tint, shade and text colour on the card
 * is derived from them, so any pair stays legible.
 */

const channels = (hex: string): [number, number, number] => {
  const h = hex.replace('#', '');
  return [0, 2, 4].map(i => parseInt(h.slice(i, i + 2), 16) || 0) as [number, number, number];
};

const toHex = (rgb: number[]) => `#${rgb.map(c => Math.round(Math.max(0, Math.min(255, c))).toString(16).padStart(2, '0')).join('')}`;

/** `a` moved toward `b` by t (0 → a, 1 → b). */
export const mix = (a: string, b: string, t: number): string => {
  const [x, y] = [channels(a), channels(b)];
  return toHex(x.map((c, i) => c + (y[i] - c) * t));
};

export const tint = (hex: string, t: number) => mix(hex, '#ffffff', t);
export const shade = (hex: string, t: number) => mix(hex, '#000000', t);

/** WCAG relative luminance. */
const luminance = (hex: string): number => {
  const [r, g, b] = channels(hex).map(c => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/** Text that reads on this colour — white on dark, near-black on light. */
export const inkOn = (hex: string): string => (luminance(hex) > 0.42 ? '#111827' : '#ffffff');

/** The colours a card is drawn in, derived once per design. */
export interface CardPalette {
  primary: string;
  /** The main colour as text on white — darkened when the colour itself is light. */
  primaryText: string;
  primaryDeep: string;
  accent: string;
  onPrimary: string;
  onPrimaryMuted: string;
  onAccent: string;
  wash: string;
  hairline: string;
  ink: string;
  inkMuted: string;
  inkFaint: string;
  paper: string;
}

export const cardPalette = (primary: string, accent: string): CardPalette => {
  const onPrimary = inkOn(primary);
  return {
    primary,
    primaryText: luminance(primary) > 0.3 ? shade(primary, 0.68) : primary,
    primaryDeep: shade(primary, 0.28),
    accent,
    onPrimary,
    onPrimaryMuted: mix(onPrimary, primary, 0.32),
    onAccent: inkOn(accent),
    wash: tint(primary, 0.93),
    hairline: tint(primary, 0.8),
    ink: '#0F172A',
    inkMuted: '#5B6473',
    inkFaint: '#8A93A3',
    paper: '#FFFFFF',
  };
};
